import { sanitizeForJson } from './sanitize.js';
import type { LogEntry } from './types.ts';

/**
 * Shape an entry for the JSON formatters.
 *
 * `message` and `data` both carry caller-supplied values, so both are sanitized
 * before `JSON.stringify` ever sees them — see {@link sanitizeForJson} for why
 * doing this beforehand rather than via a replacer is the point.
 */
function toSerializableEntry(entry: LogEntry) {
  return {
    timestamp: entry.timestamp,
    level: entry.level,
    category: entry.category,
    message: sanitizeForJson(entry.message),
    ...(entry.data?.length && { data: sanitizeForJson(entry.data) }),
  };
}

export const formatters = {
  text(entry: LogEntry): [string, ...unknown[]] {
    const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}] [${entry.category}]`;

    // If we have additional data, pass it through as separate arguments
    if (entry.data) {
      return [prefix, entry.message, ...entry.data];
    }

    // Otherwise just pass the message
    return [prefix, entry.message];
  },

  jsonl(entry: LogEntry): string {
    return JSON.stringify(toSerializableEntry(entry));
  },

  'json-pretty'(entry: LogEntry): string {
    return JSON.stringify(toSerializableEntry(entry), null, 2);
  },
};
